import { Transition } from '@headlessui/react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid'
import { classNames } from '../../util/classNames'

type Props = {
    item: any
    sidebarOpen: boolean
}
export default function NavigationItem(props: Props) {
    const { item, sidebarOpen } = props

    // Wireframe items have a normal label plus a red pill indicating that they are wireframes.
    const wireframe = Boolean(item.wireframe)

    return (
        <a
            key={item.name}
            href={item.href}
            target={item.target}
            aria-current={item.current ? 'page' : undefined}
            title={!sidebarOpen ? item.name : undefined}
            className={classNames(
                item.current
                    ? // The grey pill + red tab are drawn once by ActiveNavIndicator and
                      // slid between items; the active link just sets its own text colour
                      // and sits above the indicator (z-10) so the label reads on top.
                      'text-ink-900'
                    : 'text-ink-700 hover:bg-ink-50 hover:text-ink-900',
                // Collapsed: centre the icon in the 68px rail and drop the label's
                // reserved width so the item is a clean icon target.
                sidebarOpen ? 'gap-3 px-3' : 'justify-center px-0',
                'group relative z-10 flex w-full items-center overflow-hidden rounded-md py-2 text-sm font-medium'
            )}
        >
            <span className="relative flex-none">
                <item.icon
                    className={classNames(
                        item.current ? 'text-ink-700' : 'text-ink-400 group-hover:text-ink-600',
                        'h-5 w-5'
                    )}
                    aria-hidden="true"
                />
                {!sidebarOpen && item.badge ? (
                    <span
                        className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-severe-600"
                        aria-hidden="true"
                    />
                ) : null}
            </span>

            {/* Label is only mounted when open so the collapsed rail doesn't reserve
                width for it. The Transition fades it in on expand. */}
            {sidebarOpen && (
                <div className="min-w-0 flex-1">
                    <Transition
                        appear
                        show={sidebarOpen}
                        enter="transition-opacity duration-250"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity duration-250"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="flex w-full min-w-0 items-center">
                            <span className="truncate">{item.name}</span>

                            {wireframe && (
                                <span className="ml-auto inline-flex flex-none items-center rounded-full border border-arwen-200 bg-arwen-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-arwen-700">
                                    Wireframe
                                </span>
                            )}

                            {item.badge ? (
                                <span className="ml-auto inline-flex items-center rounded-full bg-severe-100 px-2 py-0.5 text-xs font-medium text-severe-800">
                                    {item.badge}
                                    {/* What the number counts, for screen readers:
                                        failing jobs, critical services, and so on. */}
                                    <span className="sr-only">{item.badgeLabel ?? 'failing'}</span>
                                </span>
                            ) : null}

                            {item.target === '_blank' && (
                                <>
                                    <span className="sr-only">(opens in a new tab)</span>
                                    <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4 flex-none" />
                                </>
                            )}
                        </div>
                    </Transition>
                </div>
            )}
        </a>
    )
}
